import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Eyebrow, FadeUp, MaskLine } from "./Reveal";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export const Ethos = () => {
  const { t } = useLang();
  const lines = t("ethos.lines");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="ethos" ref={ref} data-testid="about-ethos-section" className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="group relative clip-cut overflow-hidden aspect-[4/5]">
            <motion.img style={{ y }} src={useSite().img("ethos")} alt="A.R.R.O.W operators in full kit"
              className="absolute -inset-y-[12%] w-full h-[124%] object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 to-transparent" />
            <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--bone)]/85">
              FIG. 01 — A.R.R.O.W Development Group
            </p>
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          <Eyebrow>{t("ethos.eyebrow")}</Eyebrow>
          <h2 className="font-display font-black uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mt-6">
            <MaskLine>{lines[0]}</MaskLine>
            <MaskLine delay={0.1} className="text-[var(--muted)]">{lines[1]}</MaskLine>
            <MaskLine delay={0.2} className="text-[var(--muted)]">{lines[2]}</MaskLine>
            <MaskLine delay={0.3} className="text-[var(--signal)]">{lines[3]}</MaskLine>
          </h2>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-xl text-base text-[var(--muted)] leading-relaxed">
              {t("ethos.text")}
            </p>
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-3 border-t border-[var(--line)]">
            {t("ethos.principles").map(([title, d], i) => (
              <FadeUp key={i} delay={0.1 * i} className="py-6 sm:pr-6 sm:border-r last:border-r-0 border-[var(--line)] sm:pl-6 first:sm:pl-0 border-b sm:border-b-0">
                <span className="font-mono text-xs text-[var(--signal)]">0{i + 1}</span>
                <h3 className="font-display text-2xl font-bold uppercase mt-2">{title}</h3>
                <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
